import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, FormHelperText } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { ContentState, EditorState, convertToRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import useTextEditor from './useTextEditor'
import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { CleaningServices } from '@mui/icons-material'

export type TextEditorType = {
    htmlTag: string
    value: string
}
let TextEditorView: FC<any> = ({
    field,
    required,
    maxLength = 4000,
    materialInputProps = {},
    type,
    icon,
    initialRichTextEditor,
    disabled,
    isHideToolbar = false,
    label,
    helperText,
    placeholder,
    error,
    ...other
}) => {
    const { t } = useTranslation()
    const getInitialState = (content: any) => {
        const blocksFromHtml = htmlToDraft(`${content}`)
        const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap)
        return EditorState.createWithContent(contentState)
    }

    const { input, meta } = field
    const isError = meta.error && meta.invalid && meta.touched ? true : false

    const [editorState, setEditorState] = useState(() => EditorState.createEmpty())
    const MAX_LENGTH = maxLength

    /// --- set initial --- ///
    useEffect(() => {
        if (initialRichTextEditor && initialRichTextEditor.htmlTag) {
            setEditorState(getInitialState(initialRichTextEditor.htmlTag))
            input.onChange({
                htmlTag: initialRichTextEditor?.htmlTag,
                value: initialRichTextEditor?.value,
            })
        }
    }, [initialRichTextEditor])

    const getLengthOfSelectedText = () => {
        const currentSelection = editorState.getSelection()
        const isCollapsed = currentSelection.isCollapsed()

        let length = 0

        if (!isCollapsed) {
            const currentContent = editorState.getCurrentContent()
            const startKey = currentSelection.getStartKey()
            const endKey = currentSelection.getEndKey()
            const startBlock = currentContent.getBlockForKey(startKey)
            const isStartAndEndBlockAreTheSame = startKey === endKey
            const startBlockTextLength = startBlock.getLength()
            const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset()
            const endSelectedTextLength = currentSelection.getEndOffset()
            const keyAfterEnd = currentContent.getKeyAfter(endKey)
            if (isStartAndEndBlockAreTheSame) {
                length += currentSelection.getEndOffset() - currentSelection.getStartOffset()
            } else {
                let currentKey = startKey

                while (currentKey && currentKey !== keyAfterEnd) {
                    if (currentKey === startKey) {
                        length += startSelectedTextLength + 1
                    } else if (currentKey === endKey) {
                        length += endSelectedTextLength
                    } else {
                        length += currentContent.getBlockForKey(currentKey).getLength() + 1
                    }

                    currentKey = currentContent.getKeyAfter(currentKey)
                }
            }
        }

        return length
    }
    const handleBeforeInputFunc = () => {
        const currentContent = editorState.getCurrentContent()
        const currentContentLength = currentContent.getPlainText('').length
        const selectedTextLength = getLengthOfSelectedText()

        if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
            return 'handled'
        }
    }

    const handlePastedTextFunc = (pastedText: any) => {
        const currentContent = editorState.getCurrentContent()
        const currentContentLength = currentContent.getPlainText('').length
        const selectedTextLength = getLengthOfSelectedText()

        if (currentContentLength + pastedText.length - selectedTextLength > MAX_LENGTH) {
            return true
        } else {
            return false
        }
    }

    return (
        <div style={{ width: '100%' }}>
            {label && (
                <Typography>
                    {label} {!disabled && required && <label style={{ color: '#d32f2f' }}>{' *'}</label>}
                </Typography>
            )}
            <Editor
                readOnly={disabled}
                editorState={editorState}
                editorStyle={{ backgroundColor: disabled ? '#f3f3f3' : '', border: 'none' }}
                wrapperClassName="demo-wrapper"
                editorClassName="home-editor"
                placeholder={placeholder}
                // localization={{
                //     locale: 'th',
                // }}
                //@ts-ignore
                handleBeforeInput={handleBeforeInputFunc}
                handlePastedText={handlePastedTextFunc}
                onEditorStateChange={(val) => {
                    setEditorState(val)

                    const content = val.getCurrentContent()
                    const rawObject = convertToRaw(content)
                    // const htmlTag = draftToHtml(rawObject)

                    const htmlTag = 
                    content.hasText() === true ? 
                    draftToHtml(
                        rawObject,
                        {
                            trigger: '#',
                            separator: ' ',
                        },
                        true,
                        (entity, text) => {
                            if (entity.type === 'IMAGE') {
                                let textAlign = 'center'
                                if (entity.data.alignment && entity.data.alignment !== 'none') {
                                    textAlign = entity.data.alignment
                                }

                                const src = entity.data.src || ''
                                const alt = entity.data.alt || ''

                                // สมมติ width/height เป็น "300px" หรือ "300" ก็ได้
                                const w = entity.data.width
                                const h = entity.data.height

                                // ถ้าค่าเป็นตัวเลขเฉย ๆ เติม px ให้
                                const toCssSize = (v?: string | number | null): string =>
                                    v == null || v === ''
                                        ? ''
                                        : /^\d+$/.test(String(v))
                                            ? `${v}px`
                                            : String(v)

                                const widthCss = toCssSize(w)
                                const heightCss = toCssSize(h)

                                // แนวทาง responsive
                                // 1) คงความตั้งใจของ width บน desktop
                                // 2) แต่ไม่ให้เกินจอบน mobile
                                const imgStyle = [
                                    'max-width:100%',
                                    'height:auto',
                                    'display:inline-block',
                                    widthCss ? `width:min(${widthCss},100%)` : ''   // ถ้าไม่อยากใช้ min() ให้ดูวิธี 2
                                    // ไม่แนะนำใส่ height fix ถ้าต้อง responsive
                                    // heightCss ? `height:${heightCss}` : ''
                                ].filter(Boolean).join(';')

                                return `<p style="text-align:${textAlign};">
                            <img src="${src}" alt="${alt}" style="${imgStyle}"/>
                            </p>`
                            }
                        }
                    ) : null
                    const blocks = rawObject.blocks
                    const textValue = blocks.map((block) => (!block.text.trim() && '\n') || block.text).join('\n')

                    input.onChange({
                        htmlTag: htmlTag,
                        value: textValue,
                    })
                }}
                toolbarHidden={isHideToolbar}
                toolbar={{
                    options: [
                        'inline',
                        'blockType',
                        'fontSize',
                        // 'fontFamily',
                        'textAlign',
                        'colorPicker',
                        'list',
                        'link',
                        'image',
                        'embedded',
                        'history',
                        // 'emoji',
                    ],
                    fontFamily: {
                        options: [
                            // 'Sarabun',
                            'Prompt',
                            'Arial',
                            'Georgia',
                            'Impact',
                            'Tahoma',
                            'Times New Roman',
                            'Verdana',
                        ],
                    },
                    inline: {
                        options: [
                            'bold',
                            'italic',
                            'underline',
                            'strikethrough',
                            // 'code',
                            // 'monospace',
                            // 'superscript',
                            // 'subscript'
                        ],
                    },
                }}
            />
            <FormHelperText>
                <label style={{ color: '#d32f2f' }}>
                    {!disabled
                        ? required && isError && error
                            ? helperText
                                ? helperText
                                : t('LABEL.SELECT_REQUIRED')
                            : ''
                        : ''}
                </label>
            </FormHelperText>
        </div>
    )
}

let TextEditor: React.FC<any> = ({
    handleValueChange,
    name,
    required,
    validates,
    maxLength,
    isDraftNotRequire,
    ...others
}) => {
    const richTextFieldModel = useTextEditor({
        name,
        required,
        validates,
        isDraftNotRequire,
    })

    return (
        <TextEditorView
            maxLength={maxLength}
            required={required}
            handleValueChange={handleValueChange}
            {...richTextFieldModel}
            {...others}
        />
    )
}

export default TextEditor

const useStyles = makeStyles((theme) => ({
    label: {
        marginBottom: 10,
        color: (isError: any) => {
            if (isError) {
                return '#ff4d4f'
            } else {
                return ''
            }
        },
    },
    errorText: {
        fontSize: '12px',
        color: '#ff4d4f',
    },
}))
